// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import moment from "moment";
import { get } from "../common/fetch";
import Gettext from "../common/Gettext";
import timer from "../../images/timer.svg";
import { NewSimpleSpinnerIcon } from "../Spinners/SimpleSpinner";
import type { Stock as StockType } from "../../redux/CommonTypes";
import type { AugmentedAccount } from "../../redux/reducer/accounts";
import type { Dispatch } from "../../redux/action";

import { rgba } from "polished";

export const OtcImage = ({ width = 20, height = 12, tab }) => (
  <Otc tab={tab}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 12"
      width={width}
      height={height}
      fill="#3B9478"
    >
      <path d="M8.066,6.649a2.611,2.611,0,0,0-.638-1.8A2.112,2.112,0,0,0,5.772,4.13a2.077,2.077,0,0,0-1.639.716,2.633,2.633,0,0,0-.633,1.8v.623a2.622,2.622,0,0,0,.631,1.806,2.086,2.086,0,0,0,1.641.713A2.111,2.111,0,0,0,7.43,9.078a2.613,2.613,0,0,0,.636-1.806Zm-1.1.632a1.951,1.951,0,0,1-.315,1.152,1.016,1.016,0,0,1-.879.445A1,1,0,0,1,4.9,8.433a1.968,1.968,0,0,1-.3-1.155V6.641A1.963,1.963,0,0,1,4.9,5.5a.994.994,0,0,1,.867-.443,1.022,1.022,0,0,1,.879.445,1.911,1.911,0,0,1,.319,1.14v.637Zm5.613-3.066H8.4v.914H9.94V9.706h1.1V5.129h1.547V4.212Zm3.552,3.474a1.382,1.382,0,0,1-.246.89.924.924,0,0,1-.76.3.954.954,0,0,1-.822-.434A1.9,1.9,0,0,1,14,7.327V6.583a1.956,1.956,0,0,1,.289-1.105.894.894,0,0,1,.781-.434.988.988,0,0,1,.807.312,1.38,1.38,0,0,1,.259.9H17.2l.008-.025a2.023,2.023,0,0,0-.551-1.534,2.152,2.152,0,0,0-1.584-.565,1.968,1.968,0,0,0-1.562.693,2.611,2.611,0,0,0-.6,1.773v.733A2.562,2.562,0,0,0,13.527,9.1a2.048,2.048,0,0,0,1.6.689,2.057,2.057,0,0,0,1.537-.562,2.027,2.027,0,0,0,.539-1.52l0-.025H16.135ZM1.112.963H18.883a1.072,1.072,0,0,1,.79.354A1.265,1.265,0,0,1,20,2.174v9.579a1.166,1.166,0,0,1-1.115,1.211H1.112A1.166,1.166,0,0,1,0,11.752V2.174A1.166,1.166,0,0,1,1.112.963Z" />
    </svg>
  </Otc>
);

const Container = styled.div`
  width: 100%;
  padding: 100px 15px 15px;
  overflow: auto;

  @media (max-height: 800px) {
    padding-top: 30px;
  }

  @media (max-height: 700px) {
    padding-top: 0;
  }
`;

const Timer = styled.img`
  display: block;
  width: 52px;
  margin: 0 auto 22px;

  @media (max-width: 959px) {
    width: 36px;
    margin-bottom: 15px;
  }

  @media (max-width: 959px) and (orientation: landscape) {
    display: none;
  }
`;

const Text = styled.div`
  font-weight: 400;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 1.2;
  color: ${({ theme }) => theme.foreground};
  text-align: center;
`;

const TimeToOpen = styled.div`
  width: 288px;
  margin: 18px auto 23px;
  padding: 15px 5px;
  background-color: ${({ theme }) => theme.mainDark};
  border-radius: 5px;

  @media (max-width: 959px) {
    margin-bottom: 14px;
  }
`;

const TimeText = styled.div`
  margin-top: 5px;
  font-size: 16px;
  line-height: 1.2;
  text-align: center;
  color: ${({ theme }) => theme.stockCloseTimeColor};
`;

const Line = styled.hr`
  max-width: 558px;
  height: 1px;
  margin: 0 auto 23px;
  background-color: rgba(145, 145, 145, 0.59);
  border: none;

  @media (max-width: 959px) {
    display: none;
  }
`;

export const StocksTable = styled.div`
  width: 100%;
  max-width: 303px;
  margin: 18px auto 0;

  @media (max-width: 959px) {
    margin-top: 10px;
  }
`;

export const Stock = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
  padding: 8px 12px;
  background-color: rgba(145, 145, 145, 0.3);
  cursor: pointer;

  :last-of-type {
    margin-bottom: 0;
  }

  :hover {
    color: #feac00;

    > div {
      color: #feac00;
    }
  }
`;

export const StockCategory = styled.div`
  width: 40px;
  white-space: nowrap;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  text-transform: uppercase;
  color: ${({ theme }) => rgba(theme.foreground, 0.7)};
`;

export const StockName = styled.div`
  position: relative;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  text-transform: uppercase;
  color: ${({ theme }) => theme.foreground};
`;

export const StockPercent = styled.div`
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  text-transform: uppercase;
  color: ${({ theme }) => rgba(theme.foreground, 0.7)};
`;

export const Otc = styled.div`
  position: absolute;
  right: 75px;
  top: 50%;
  width: 20px;
  height: 12px;
  margin-top: -7px;
  font-size: 0;
  line-height: 0;

  ${props =>
    props.tab &&
    css`
      right: 9px;
      top: auto;
      bottom: 7px;
      margin-top: 0;
    `};

  @media (max-width: 359px) {
    right: 55px;
  }
`;

const AnotherContainer = styled.div`
  text-align: center;
`;

const SimpleSpinner = styled.div`
  width: 30px;
  height: 30px;
  margin: 0 auto;
  color: rgba(145, 145, 145, 0.59);
`;

type Props = {
  stocks: { [string]: StockType },
  onStockClick: string => void,
  activeAccount: AugmentedAccount,
  stock: StockType,
  dispatch: Dispatch
};

class StockClose extends React.Component<Props> {
  state = { ready: false };

  refresh() {
    return get("/api/getAssets").then(payload => {
      this.props.dispatch({ type: "RECEIVE_STOCK_ASSETS", payload });
      return payload;
    });
  }

  componentDidMount() {
    if (this.props.stock.enabled === false) {
      this.refresh().then(() => {
        this.setState({ ready: true });
      });
    }
  }

  componentDidUpdate(prevProps) {
    // Бывает такое, что мы показываем экран оптимистично, по таймеру.
    // После этого мы получим актуальный апдейт и сделаем запрос для всех
    // остальных котировок.
    if (
      prevProps.stock.enabled === true &&
      this.props.stock.enabled === false
    ) {
      this.refresh().then(() => {
        this.setState({ ready: true });
      });
    }
  }

  render() {
    const props = this.props;

    const recommendations = this.state.ready
      ? Object.keys(props.stocks)
          .map(symbol => props.stocks[symbol])
          .filter(stock => stock.enabled && !props.tabs.includes(stock.stock))
          .sort(
            (a, b) =>
              props.activeAccount.type === "real"
                ? b.realProfit - a.realProfit
                : b.demoProfit - a.demoProfit
          )
          .slice(0, 6)
      : [];

    return (
      <Container>
        <Timer src={timer} alt="" />
        <Text>
          <Gettext>The asset trading is closed now</Gettext>
        </Text>
        {props.stock.state_changed_at != null ? (
          <TimeToOpen>
            <Text>
              <Gettext>Trading will be opened at</Gettext>:
            </Text>
            <TimeText>
              {moment(props.stock.state_changed_at * 1000).format(
                "MMMM Do, HH:mm"
              )}
            </TimeText>
          </TimeToOpen>
        ) : null}

        <Line />

        {recommendations.length > 0 ? (
          <React.Fragment>
            <Text>
              <Gettext>You can use the following assets</Gettext>:
            </Text>
            <StocksTable>
              {recommendations.map(stock => (
                <Stock
                  key={stock.stock}
                  onClick={() => {
                    if (typeof props.onStockClick === "function") {
                      props.onStockClick(stock.stock);
                    }
                  }}
                >
                  <StockCategory>{stock.category}</StockCategory>
                  <StockName>{stock.stock.replace("_OTC", "")}</StockName>
                  {stock.otc ? <OtcImage /> : <Otc />}
                  <StockPercent>
                    {props.activeAccount.type === "real"
                      ? `${stock.realProfit}%`
                      : `${stock.demoProfit}%`}
                  </StockPercent>
                </Stock>
              ))}
            </StocksTable>
          </React.Fragment>
        ) : (
          <AnotherContainer>
            <SimpleSpinner>
              <NewSimpleSpinnerIcon width="30px" height="30px" />
            </SimpleSpinner>
          </AnotherContainer>
        )}
      </Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    tabs: state.tabs && state.tabs.tabs
  };
}

export default connect(mapStateToProps)(StockClose);



// WEBPACK FOOTER //
// ./src/components/Trade/StockClose.js